/**
 * Анон-режим витрины отеля (#323/#12) — публичные страницы БЕЗ логина.
 *
 * Открывается по роуту `/anonymous` (вне RequireAuth) на поддомене отеля
 * `<slug>.lovarthotels.com`. Тенант резолвится по поддомену через @hsc/tenant
 * (TenantProvider) — JWT не требуется и не отправляется.
 *
 * 4 раздела (вкладки):
 *   1. Номера   — GET /v1/public/{slug}/room-types
 *   2. Афиша    — GET /v1/public/{slug}/events
 *   3. Об отеле — GET /v1/public/{slug}/hotel-info  (data-testid="hotel-info-card")
 *   4. Бронь    — POST /v1/public/{slug}/booking-lead (форма booking-lead-form)
 *
 * Дизайн — токены/компоненты D5b (@hsc/ui), тон Friendly, ru-локаль.
 */
import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, Input, Skeleton } from '@hsc/ui';
import { useTenant } from '@hsc/tenant';
import type { Event, PublicHotelInfo, PublicRoomType } from '@hsc/types';
import {
  usePublicEvents,
  usePublicHotelInfo,
  usePublicRoomTypes,
  useCreateBookingLead,
} from './useAnon.js';
import { PublicTrustFooter } from '../../shared/ui/PublicTrustFooter.js';
import { PhotoLightbox } from '../rooms/PhotoLightbox.js';
import { amenityLabels, photoUrl } from '../../lib/fleet-format.js';

type TabKey = 'rooms' | 'events' | 'about' | 'booking';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'rooms', label: 'Номера' },
  { key: 'events', label: 'Афиша' },
  { key: 'about', label: 'Об отеле' },
  { key: 'booking', label: 'Забронировать' },
];

const EVENT_CATEGORY_LABEL: Record<string, string> = {
  party: 'Вечеринка',
  lecture: 'Лекция',
  kids: 'Детям',
  sport: 'Спорт',
  spa: 'СПА',
  general: 'Общее',
  excursion: 'Экскурсия',
};

/** Цена из копеек в рубли (диапазон, если min ≠ max). */
function formatPrice(rt: PublicRoomType): string {
  const fmt = (kop: number): string =>
    new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: rt.currency || 'RUB',
      maximumFractionDigits: 0,
    }).format(kop / 100);
  return rt.min_price_kopecks === rt.max_price_kopecks
    ? fmt(rt.min_price_kopecks)
    : `${fmt(rt.min_price_kopecks)} — ${fmt(rt.max_price_kopecks)}`;
}

/** Дата-время события в ru-формате. */
function formatEventDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

export function AnonymousMode(): JSX.Element {
  const { slug, tenant } = useTenant();
  const [tab, setTab] = useState<TabKey>('rooms');

  const hotelName = tenant?.displayName ?? slug ?? 'Отель';

  return (
    <main className="anon" data-testid="anonymous-mode">
      <header className="anon__header">
        <div className="anon__brand">
          <span aria-hidden="true" className="hsc-login__logo">L</span>
          <div>
            <h1 className="anon__title">{hotelName}</h1>
            <p className="anon__subtitle">Знакомство с отелем — без регистрации</p>
          </div>
        </div>
        <Link to="/login" className="anon__login-link" data-testid="anon-login-link">
          Вход для гостей и команды
        </Link>
      </header>

      <nav className="anon__tabs" aria-label="Разделы">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`anon__tab${tab === t.key ? ' anon__tab--active' : ''}`}
            aria-current={tab === t.key ? 'page' : undefined}
            data-testid={`anon-tab-${t.key}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <section className="anon__body">
        {tab === 'rooms' && <RoomsSection slug={slug} />}
        {tab === 'events' && <EventsSection slug={slug} />}
        {tab === 'about' && <AboutSection slug={slug} />}
        {tab === 'booking' && <BookingSection slug={slug} />}
      </section>

      <PublicTrustFooter />
    </main>
  );
}

/* ===================== 1. Номера ===================== */

function RoomsSection({ slug }: { slug: string | null }): JSX.Element {
  const { data, isLoading, isError } = usePublicRoomTypes(slug);
  const [gallery, setGallery] = useState<{ photos: string[]; title: string } | null>(null);

  if (isLoading) {
    return (
      <div className="cat-grid" data-testid="anon-rooms-skeleton" aria-busy="true">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} height={180} radius={20} />
        ))}
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <p role="alert">Не удалось загрузить номера. Попробуйте обновить страницу.</p>
      </Card>
    );
  }
  if (data.room_types.length === 0) {
    return (
      <Card>
        <p>Отель ещё наполняет каталог номеров. Загляните чуть позже.</p>
      </Card>
    );
  }

  return (
    <>
    <div className="cat-grid" data-testid="anon-rooms">
      {data.room_types.map((rt) => {
        const photos = (rt.photos ?? [])
          .map((ph) => photoUrl(ph.object_key))
          .filter((u): u is string => Boolean(u));
        const cover = photos[0];
        const amenities = amenityLabels(rt.amenities);
        const title = rt.display_name?.ru ?? rt.name;
        return (
          <Card key={rt.id} data-testid={`anon-room-${rt.code}`}>
            {cover && (
              <button
                type="button"
                className="anon__room-photo-btn"
                onClick={() => setGallery({ photos, title })}
                aria-label={`Открыть фото — ${title}`}
                data-testid={`anon-room-${rt.code}-photo`}
              >
                <img className="anon__room-photo" src={cover} alt={title} loading="lazy" />
                {photos.length > 1 && (
                  <span className="anon__room-photo-count">{photos.length} фото</span>
                )}
              </button>
            )}
            <h3 className="anon__room-name">{rt.display_name?.ru ?? rt.name}</h3>
            {rt.description && <p className="anon__room-desc">{rt.description}</p>}
            <p className="anon__room-meta">Гостей: до {rt.capacity_max}</p>
            {amenities.length > 0 && (
              <ul className="anon__room-amenities" data-testid={`anon-room-${rt.code}-amenities`}>
                {amenities.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            )}
            <p className="anon__room-price">{formatPrice(rt)} / ночь</p>
          </Card>
        );
      })}
    </div>
    {gallery && gallery.photos.length > 0 && (
      <PhotoLightbox
        photos={gallery.photos}
        index={0}
        title={gallery.title}
        onClose={() => setGallery(null)}
      />
    )}
    </>
  );
}

/* ===================== 2. Афиша ===================== */

function EventsSection({ slug }: { slug: string | null }): JSX.Element {
  const { data, isLoading, isError } = usePublicEvents(slug);

  if (isLoading) {
    return (
      <div className="anon__list" data-testid="anon-events-skeleton" aria-busy="true">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} height={84} radius={16} />
        ))}
      </div>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <p role="alert">Не удалось загрузить афишу. Попробуйте обновить страницу.</p>
      </Card>
    );
  }
  if (data.events.length === 0) {
    return (
      <Card>
        <p>Пока запланированных событий нет. Загляните позже — мы что-нибудь придумаем.</p>
      </Card>
    );
  }

  return (
    <div className="anon__list" data-testid="anon-events">
      {data.events.map((ev: Event) => (
        <Card key={ev.id} data-testid={`anon-event-${ev.id}`}>
          <span className="anon__event-cat">
            {EVENT_CATEGORY_LABEL[ev.category] ?? ev.category}
          </span>
          <h3 className="anon__event-title">{ev.title}</h3>
          <p className="anon__event-date">{formatEventDate(ev.starts_at)}</p>
          {ev.description && <p className="anon__event-desc">{ev.description}</p>}
        </Card>
      ))}
    </div>
  );
}

/* ===================== 3. Об отеле ===================== */

function AboutSection({ slug }: { slug: string | null }): JSX.Element {
  const { data, isLoading, isError } = usePublicHotelInfo(slug);

  if (isLoading) {
    return <Skeleton height={260} radius={20} />;
  }
  if (isError || !data) {
    return (
      <Card>
        <p role="alert">Не удалось загрузить информацию об отеле. Попробуйте обновить страницу.</p>
      </Card>
    );
  }

  const info: PublicHotelInfo = data;
  return (
    <Card data-testid="hotel-info-card">
      <h2 className="hsc-card__title">{info.display_name}</h2>
      {info.description && <p className="anon__about-desc">{info.description}</p>}

      {info.photos.length > 0 && (
        <div className="anon__photos" data-testid="hotel-info-photos">
          {info.photos.map((src, i) => (
            <img key={`${src}-${i}`} src={src} alt={`${info.display_name} — фото ${i + 1}`} loading="lazy" />
          ))}
        </div>
      )}

      <dl className="anon__about-grid">
        {info.address && (
          <>
            <dt>Адрес</dt>
            <dd data-testid="hotel-info-address">{info.address}</dd>
          </>
        )}
        {info.phone.length > 0 && (
          <>
            <dt>Телефон</dt>
            <dd data-testid="hotel-info-phone">
              {info.phone.map((p, i) => (
                <a key={p} href={`tel:${p.replace(/\s/g, '')}`}>
                  {p}
                  {i < info.phone.length - 1 ? ', ' : ''}
                </a>
              ))}
            </dd>
          </>
        )}
        {info.email && (
          <>
            <dt>Email</dt>
            <dd data-testid="hotel-info-email">
              <a href={`mailto:${info.email}`}>{info.email}</a>
            </dd>
          </>
        )}
      </dl>

      {info.amenities.length > 0 && (
        <ul className="anon__amenities" data-testid="hotel-info-amenities">
          {info.amenities.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ===================== 4. Бронь ===================== */

interface LeadFormState {
  name: string;
  phone: string;
  email: string;
  room_type_code: string;
  check_in: string;
  guests: string;
  message: string;
}

const EMPTY_LEAD: LeadFormState = {
  name: '',
  phone: '',
  email: '',
  room_type_code: '',
  check_in: '',
  guests: '',
  message: '',
};

function BookingSection({ slug }: { slug: string | null }): JSX.Element {
  const roomTypes = usePublicRoomTypes(slug);
  const mutation = useCreateBookingLead(slug);
  const [form, setForm] = useState<LeadFormState>(EMPTY_LEAD);
  const [clientError, setClientError] = useState<string | null>(null);

  const serverContactRequired = useMemo(() => {
    const err = mutation.error as unknown as { status?: number; body?: { error?: string } } | null;
    return err?.status === 400 && err?.body?.error === 'booking_lead.contact_required';
  }, [mutation.error]);

  const set =
    (k: keyof LeadFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setClientError(null);
    if (form.name.trim() === '') {
      setClientError('Укажите, как Вас зовут');
      return;
    }
    if (form.phone.trim() === '' && form.email.trim() === '') {
      setClientError('Укажите телефон или почту, чтобы мы могли связаться с Вами');
      return;
    }
    const body = {
      name: form.name.trim(),
      ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
      ...(form.email.trim() ? { email: form.email.trim() } : {}),
      ...(form.room_type_code ? { room_type_code: form.room_type_code } : {}),
      ...(form.check_in ? { check_in: form.check_in } : {}),
      ...(form.guests.trim() ? { guests: Number(form.guests) } : {}),
      ...(form.message.trim() ? { message: form.message.trim() } : {}),
    };
    mutation.mutate(body);
  };

  if (mutation.isSuccess) {
    return (
      <Card data-testid="booking-lead-success">
        <h2 className="hsc-card__title">Заявка принята</h2>
        <p>
          Спасибо! Мы получили Вашу заявку и свяжемся с Вами в ближайшее время.
          {mutation.data?.lead_id ? ` Номер заявки: ${mutation.data.lead_id}.` : ''}
        </p>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            mutation.reset();
            setForm(EMPTY_LEAD);
          }}
        >
          Отправить ещё одну
        </Button>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="hsc-card__title">Заявка на бронирование</h2>
      <p className="anon__about-desc">
        Оставьте контакты — менеджер отеля свяжется с Вами и подберёт номер. Онлайн-оплата не требуется.
      </p>
      <form className="hsc-stack" onSubmit={onSubmit} noValidate data-testid="booking-lead-form">
        <Input
          label="Ваше имя"
          name="name"
          value={form.name}
          onChange={set('name')}
          placeholder="Анна"
          data-testid="booking-lead-name"
        />
        <Input
          label="Телефон"
          name="phone"
          type="tel"
          value={form.phone}
          onChange={set('phone')}
          placeholder="+7 999 123-45-67"
          data-testid="booking-lead-phone"
        />
        <Input
          label="Email"
          name="email"
          type="email"
          value={form.email}
          onChange={set('email')}
          placeholder="anna@example.com"
          data-testid="booking-lead-email"
        />

        <label className="anon__field">
          <span className="hsc-input__label">Тип номера (необязательно)</span>
          <select
            className="anon__select"
            value={form.room_type_code}
            onChange={set('room_type_code')}
            data-testid="booking-lead-room-type"
          >
            <option value="">Не выбран</option>
            {(roomTypes.data?.room_types ?? []).map((rt) => (
              <option key={rt.code} value={rt.code}>
                {rt.display_name?.ru ?? rt.name}
              </option>
            ))}
          </select>
        </label>

        <Input
          label="Дата заезда (необязательно)"
          name="check_in"
          type="date"
          value={form.check_in}
          onChange={set('check_in')}
          data-testid="booking-lead-check-in"
        />
        <Input
          label="Гостей (необязательно)"
          name="guests"
          type="number"
          value={form.guests}
          onChange={set('guests')}
          placeholder="2"
          data-testid="booking-lead-guests"
        />

        <label className="anon__field">
          <span className="hsc-input__label">Комментарий (необязательно)</span>
          <textarea
            className="anon__textarea"
            value={form.message}
            onChange={set('message')}
            rows={3}
            placeholder="Например: хотим номер с видом на море"
            data-testid="booking-lead-message"
          />
        </label>

        {(clientError ?? (serverContactRequired ? 'Укажите телефон или почту, чтобы мы могли связаться с Вами' : null)) && (
          <div role="alert" className="hsc-input__error" data-testid="booking-lead-error">
            {clientError ?? 'Укажите телефон или почту, чтобы мы могли связаться с Вами'}
          </div>
        )}
        {mutation.isError && !serverContactRequired && (
          <div role="alert" className="hsc-input__error">
            Не удалось отправить заявку. Попробуйте ещё раз позже.
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" loading={mutation.isPending}>
          Отправить заявку
        </Button>
      </form>
    </Card>
  );
}
